import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Trophy, X, Sparkles } from "lucide-react";
import { useAuth } from "../App";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * JackpotWinnerModal
 * Polls /api/jackpot/my-wins every 15s for unacknowledged personal jackpot
 * wins for the CURRENT logged-in user. When a win is found, shows a giant
 * fireworks celebration modal with the SC prize amount. Dismissing the modal
 * acknowledges the win on the backend so it never re-shows.
 */
export const JackpotWinnerModal = () => {
  const { token } = useAuth();
  const [win, setWin] = useState(null);
  const [dismissing, setDismissing] = useState(false);
  const seenIdsRef = useRef(new Set());

  const fetchWins = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/api/jackpot/my-wins`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (Array.isArray(res.data) && res.data.length > 0) {
        const latest = res.data[0];
        if (latest?.id && !seenIdsRef.current.has(latest.id)) {
          seenIdsRef.current.add(latest.id);
          setWin(latest);
        }
      }
    } catch (e) {
      // silently ignore — user might not be authed yet
    }
  }, [token]);

  useEffect(() => {
    if (!token) return;
    fetchWins();
    const interval = setInterval(fetchWins, 15000);
    return () => clearInterval(interval);
  }, [token, fetchWins]);

  const dismiss = async () => {
    if (!win || dismissing) return;
    setDismissing(true);
    try {
      await axios.post(`${API}/api/jackpot/my-wins/${win.id}/ack`, null, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (e) {
      // ignore — still close the modal so it doesn't trap the user
    }
    setTimeout(() => {
      setWin(null);
      setDismissing(false);
    }, 400);
  };

  if (!win) return null;

  const tier = (win.jackpot_type || "minor").toUpperCase();
  const amount = Number(win.amount || 0);
  const tierColor = {
    MINOR: "from-amber-400 via-yellow-500 to-orange-500",
    MAJOR: "from-fuchsia-400 via-pink-500 to-rose-500",
    MEGA: "from-emerald-300 via-teal-400 to-cyan-500",
  }[tier] || "from-amber-400 via-yellow-500 to-orange-500";

  // 24 firework bursts scattered around the modal
  const bursts = Array.from({ length: 24 });

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 backdrop-blur-md bg-black/70 animate-fade-in"
      data-testid="jackpot-winner-modal"
    >
      {/* Firework bursts */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {bursts.map((_, i) => (
          <div
            key={i}
            className="absolute firework"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${(i % 8) * 0.35}s`,
              animationDuration: `${1.6 + (i % 5) * 0.25}s`,
              background: [
                "radial-gradient(circle, #fde047 0%, transparent 70%)",
                "radial-gradient(circle, #f472b6 0%, transparent 70%)",
                "radial-gradient(circle, #34d399 0%, transparent 70%)",
                "radial-gradient(circle, #60a5fa 0%, transparent 70%)",
                "radial-gradient(circle, #fb923c 0%, transparent 70%)",
              ][i % 5],
            }}
          />
        ))}
      </div>

      {/* Confetti strips */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {Array.from({ length: 40 }).map((_, i) => (
          <div
            key={i}
            className="absolute confetti"
            style={{
              left: `${Math.random() * 100}%`,
              top: "-10%",
              width: "8px",
              height: "14px",
              background: [
                "#fde047",
                "#f472b6",
                "#34d399",
                "#60a5fa",
                "#fb923c",
                "#a78bfa",
              ][i % 6],
              animationDelay: `${Math.random() * 2}s`,
              animationDuration: `${3 + Math.random() * 2}s`,
              transform: `rotate(${Math.random() * 360}deg)`,
            }}
          />
        ))}
      </div>

      {/* Modal card */}
      <div
        className="relative w-full max-w-md rounded-3xl overflow-hidden shadow-2xl border-2 border-amber-400/60 animate-pop-in"
        style={{
          background:
            "linear-gradient(135deg, rgba(24,10,40,0.98) 0%, rgba(60,20,10,0.98) 60%, rgba(80,40,10,0.98) 100%)",
          boxShadow:
            "0 0 60px rgba(251,191,36,0.6), 0 0 120px rgba(251,113,133,0.4)",
        }}
      >
        {/* Close */}
        <button
          onClick={dismiss}
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/70 hover:text-white transition-colors"
          data-testid="jackpot-winner-close"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-8 text-center">
          {/* Trophy */}
          <div
            className={`mx-auto w-24 h-24 rounded-full bg-gradient-to-br ${tierColor} flex items-center justify-center shadow-2xl mb-4 animate-trophy-bounce`}
          >
            <Trophy className="w-12 h-12 text-white drop-shadow-lg" />
          </div>

          {/* Tier badge */}
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-400/20 border border-amber-400/40 text-amber-200 text-[11px] font-bold uppercase tracking-widest mb-3">
            <Sparkles className="w-3 h-3" /> {tier} Grand Prize
          </div>

          {/* You Won */}
          <h2 className="text-3xl sm:text-4xl font-black text-white mb-2 tracking-tight">
            YOU WON!
          </h2>
          <p className="text-sm text-amber-200/80 mb-5">
            Congratulations {win.username ? win.username : ""} — your prize has been credited.
          </p>

          {/* Amount */}
          <div
            className={`text-5xl sm:text-6xl font-black bg-clip-text text-transparent bg-gradient-to-r ${tierColor} mb-1 drop-shadow-[0_4px_20px_rgba(251,191,36,0.6)] animate-amount-glow`}
            data-testid="jackpot-winner-amount"
          >
            {amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div className="text-amber-300 text-lg font-bold uppercase tracking-widest mb-6">
            Sweeps Coins
          </div>

          <button
            onClick={dismiss}
            disabled={dismissing}
            className="w-full py-3 rounded-xl font-bold text-black text-lg bg-gradient-to-r from-amber-300 via-yellow-400 to-amber-500 hover:brightness-110 transition-all shadow-lg shadow-amber-500/40 disabled:opacity-60"
            data-testid="jackpot-winner-collect"
          >
            {dismissing ? "Collecting..." : "Awesome, Collect Prize"}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        .animate-fade-in { animation: fadeIn 0.35s ease-out; }
        @keyframes popIn {
          0% { opacity: 0; transform: scale(0.7) translateY(20px); }
          60% { opacity: 1; transform: scale(1.04) translateY(-4px); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
        .animate-pop-in { animation: popIn 0.55s cubic-bezier(0.34, 1.56, 0.64, 1); }
        @keyframes trophyBounce {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-8px) scale(1.05); }
        }
        .animate-trophy-bounce { animation: trophyBounce 1.6s ease-in-out infinite; }
        @keyframes amountGlow {
          0%, 100% { text-shadow: 0 0 10px rgba(251,191,36,0.4); }
          50% { text-shadow: 0 0 30px rgba(251,191,36,0.9), 0 0 60px rgba(251,113,133,0.5); }
        }
        .animate-amount-glow { animation: amountGlow 1.8s ease-in-out infinite; }

        .firework {
          position: absolute;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          opacity: 0;
          animation-name: firework;
          animation-iteration-count: infinite;
          animation-timing-function: ease-out;
        }
        @keyframes firework {
          0% { opacity: 0; transform: scale(0.1); box-shadow: 0 0 0 0 rgba(255,255,255,0); }
          20% { opacity: 1; transform: scale(1); }
          60% { opacity: 0.7; transform: scale(20); box-shadow: 0 0 40px 10px currentColor; }
          100% { opacity: 0; transform: scale(30); }
        }

        .confetti {
          position: absolute;
          top: -10%;
          opacity: 0.9;
          animation-name: confettiFall;
          animation-iteration-count: infinite;
          animation-timing-function: linear;
          border-radius: 2px;
        }
        @keyframes confettiFall {
          0% { transform: translateY(-100px) rotate(0deg); opacity: 1; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export default JackpotWinnerModal;
